import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { AccountsApiService } from './api/accounts-api.service';
import { CoreApiService } from './api/core-api.service';
import { HubApiService } from './api/hub-api.service';
import { JobPageApiService } from './api/job-page-api.service';
import { InitializeJobPagePayload } from '../models';

export interface ApiResponse<T = unknown> {
  success: boolean;
  data: T;
  message?: string;
  error?: string;
}

@Injectable({
  providedIn: 'root',
})
export class ApiService {
  constructor(
    private readonly http: HttpClient,
    private readonly jobPageApi: JobPageApiService,
    private readonly hubApi: HubApiService,
    private readonly coreApi: CoreApiService,
    private readonly accountsApi: AccountsApiService
  ) {}

  get<T>(url: string, params?: HttpParams): Observable<T> {
    return this.http.get<T>(url, { params });
  }

  post<T>(url: string, body: unknown): Observable<T> {
    return this.http.post<T>(url, body);
  }

  put<T>(url: string, body: unknown): Observable<T> {
    return this.http.put<T>(url, body);
  }

  delete<T>(url: string): Observable<T> {
    return this.http.delete<T>(url);
  }

  patch<T>(url: string, body: unknown): Observable<T> {
    return this.http.patch<T>(url, body);
  }

  updateTemplateBychat<T>(payload: unknown): Observable<T> {
    return this.jobPageApi.updateTemplateBychat<T>(payload);
  }

  getAllLandingPages<T>(payload: unknown): Observable<T> {
    return this.jobPageApi.getAllLandingPages<T>(payload);
  }

  getPageDetailsById<T>(uniqueCode: string): Observable<T> {
    return this.jobPageApi.getPageDetailsById<T>(uniqueCode);
  }

  getPublicPageDetailsById<T>(uniqueCode: string): Observable<T> {
    return this.jobPageApi.getPublicPageDetailsById<T>(uniqueCode);
  }

  deleteLandingPage<T>(id: number): Observable<T> {
    return this.jobPageApi.deleteLandingPage<T>(id);
  }

  updateLandingPageData<T>(
    payload: unknown,
    actionType?: string
  ): Observable<T> {
    return this.jobPageApi.updateLandingPageData<T>(payload, actionType);
  }

  promptConfiguration<T>(
    method: 'GET' | 'PUT',
    adminPassword: string,
    payload?: { id?: number; [key: string]: unknown }
  ): Observable<T> {
    return this.jobPageApi.promptConfiguration<T>(
      method,
      adminPassword,
      payload
    );
  }

  getTemplatesByClient<T>(payload: unknown): Observable<T> {
    return this.jobPageApi.getTemplatesByClient<T>(payload);
  }

  getTemplateDetailsById<T>(uniqueCode: string): Observable<T> {
    return this.jobPageApi.getTemplateDetailsById<T>(uniqueCode);
  }

  getDomainsList<T>(clientCode: string): Observable<T> {
    return this.accountsApi.getDomainsList<T>(clientCode);
  }

  getVacancies<T>(payload: {
    clientCode: string;
    [key: string]: unknown;
  }): Observable<T> {
    return this.coreApi.getVacancies<T>(payload);
  }

  getClientLanguages<T>(clientCode: string): Observable<T> {
    return this.coreApi.getClientLanguages<T>(clientCode);
  }

  getVariableFields<T>(
    clientCode: string,
    objects: string = 'Contact'
  ): Observable<T> {
    return this.hubApi.getVariableFields<T>(clientCode, objects);
  }

  getUserInfo<T>(token: string, clientCode: string): Observable<T> {
    return this.hubApi.getUserInfo<T>(token, clientCode);
  }

  upsertPagePublish<T>(payload: unknown): Observable<T> {
    return this.jobPageApi.upsertPagePublish<T>(payload);
  }

  leadInsert<T>(payload: {
    clientCode: string;
    [key: string]: unknown;
  }): Observable<T> {
    return this.hubApi.leadInsert<T>(payload);
  }

  contactInsert<T>(
    payload: Record<string, unknown>,
    endpointUrl?: string
  ): Observable<T> {
    return this.hubApi.contactInsert<T>(payload, endpointUrl);
  }

  checkPageNameKey<T>(params: {
    page_id: number;
    short_key: string;
    domain_name: string;
  }): Observable<T> {
    return this.jobPageApi.checkPageNameKey<T>(params);
  }

  checkFolioUrlSettingKey<T>(params: {
    page_id?: number;
    short_key?: string;
    domain_name?: string;
    FolioId?: any;
    Key?: string;
    DomainName?: string;
  }): Observable<T> {
    return this.jobPageApi.checkFolioUrlSettingKey<T>(params);
  }

  upsertCustomFont<T>(payload: unknown): Observable<T> {
    return this.jobPageApi.upsertCustomFont<T>(payload);
  }

  getCustomFontList<T>(payload: {
    client_code: string;
    pageIndex: number;
    pageSize: number;
  }): Observable<T> {
    return this.jobPageApi.getCustomFontList<T>(payload);
  }

  deleteCustomFont<T>(fontId: string | number): Observable<T> {
    return this.jobPageApi.deleteCustomFont<T>(fontId);
  }

  initializePageBuilder<T>(payload: InitializeJobPagePayload): Observable<T> {
    return this.jobPageApi.initializePageBuilder<T>(payload);
  }

  getAgentChatHistory<T>(sessionId: string): Observable<T> {
    return this.jobPageApi.getAgentChatHistory<T>(sessionId);
  }

  startChatWithAgent<T>(payload: unknown): Observable<T> {
    return this.jobPageApi.startChatWithAgent<T>(payload);
  }

  createNewTemplate<T>(payload: unknown): Observable<T> {
    return this.jobPageApi.createNewTemplate<T>(payload);
  }

  generatePrerenderedPageUrl<T>(pageUrl: string): Observable<T> {
    return this.jobPageApi.generatePrerenderedPageUrl<T>(pageUrl);
  }

  uploadMediaOnCloudinary<T>(payload: unknown): Observable<T> {
    return this.jobPageApi.uploadMediaOnCloudinary<T>(payload);
  }

  unpublishPage<T>(payload: unknown): Observable<T> {
    return this.jobPageApi.unpublishPage<T>(payload);
  }

  changePageVacancy<T>(payload: {
    id: number;
    client_code: string;
    title: string;
    source_id: string;
    source_title?: string;
  }): Observable<T> {
    return this.jobPageApi.changePageVacancy<T>(payload);
  }

  deleteTemplate<T>(templateId: number): Observable<T> {
    return this.jobPageApi.deleteTemplate<T>(templateId);
  }

  updateTemplate<T>(payload: unknown): Observable<T> {
    return this.jobPageApi.updateTemplate<T>(payload);
  }

  resolveVacancyPrompt<T>(
    templateId: number | string,
    payload: { vacancyId: string }
  ): Observable<T> {
    return this.jobPageApi.resolveVacancyPrompt<T>(templateId, payload);
  }
}
